import {
  defineNuxtPlugin,
  useRoute,
  useRouter,
  useRuntimeConfig,
} from '#imports'

import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import {
  type EventEvent,
  type LogEvent,
  type MeasurementEvent,
  TransportItemType,
  getWebInstrumentations,
  initializeFaro,
} from '@grafana/faro-web-sdk'

import { getSamplingRateForRoute } from '../utils/faroSamplingRules'
import { getCacheStatus } from '../utils/getCacheStatus'
import { getConnection } from '../utils/getConnection'
import { getDeviceType } from '../utils/getDeviceType'
import { getRoutePath } from '../utils/getRoutePath'
import { getVerifiedBot } from '../utils/getVerifiedBot'
import { isCloudflareReportCspViolation } from '../utils/isCloudflareReportCspViolation'

export default defineNuxtPlugin({
  name: 'instrumentation:faro',
  // Run before any other client plugin so `faro.api` is available when the
  // logger / error plugins emit their first events. Without this, early
  // `logger.error(...)` calls hit the Faro transport before `initializeFaro()`
  // has completed and are silently dropped by its `if (!faro?.api) return`
  // guard.
  enforce: 'pre',
  setup() {
    if (!process.client) return

    const publicConfig = useRuntimeConfig().public
    const config = publicConfig.instrumentation
    const route = useRoute()
    const router = useRouter()

    if (!config.grafanaFaroEnabled || !config.grafanaFrontendApplicationUrl)
      return

    // Captured once per page to avoid redundant DOM/window reads per metric.
    const webVitalsContext = {
      connection: getConnection(),
      deviceType: getDeviceType(),
      cachestatus: getCacheStatus(),
      verifiedbot: getVerifiedBot(),
    }
    const routePath = getRoutePath(route)
    const locale = useI18nLocale()

    initializeFaro({
      url: config.grafanaFrontendApplicationUrl,
      app: {
        name: publicConfig.SERVICE,
        version: publicConfig.VERSION,
        environment: publicConfig.ENVIRONMENT,
      },
      instrumentations: [
        ...getWebInstrumentations({
          // already propagated via front-logger library
          captureConsole: false,
        }),
      ],
      sessionTracking: {
        sampler() {
          // router.currentRoute is a reactive ref captured at plugin init time.
          // We use it instead of useRoute() because the sampler can be called
          // from Faro's metas listener (outside Vue setup context), where
          // useRoute() would fail.
          const currentRoutePath = getRoutePath(router.currentRoute.value)

          return (
            getSamplingRateForRoute(
              config.faroSamplingRules ?? [],
              currentRoutePath,
            ) ?? Number(config.samplingRate)
          )
        },
      },

      pageTracking: {
        page: {
          id: routePath,
          attributes: {
            locale,
          },
        },
      },

      view: {
        name: routePath,
      },

      ignoreErrors: [
        // Harmless browser layout quirk, not a real error
        /^ResizeObserver loop limit exceeded$/,
        /^ResizeObserver loop completed with undelivered notifications$/,
        // Cross-origin scripts with no useful stack
        /^Script error\.$/,
        // Browser extension interference
        /chrome-extension:\/\//,
        /moz-extension:\/\//,
        // iOS Edge translation — throws in bursts
        /^Promise does not exist for the provided text/,
        // Facebook/Instagram webview script injections
        /^FBNav/,
      ],

      beforeSend: (item) => {
        if (item.type === TransportItemType.LOG) {
          const log = item.payload as LogEvent

          // Facebook/Instagram webview injections (console.info calls)
          if (log.message.startsWith('FBNav')) return null

          // iOS Edge translation logs (error variant covered by ignoreErrors)
          if (
            log.message.startsWith(
              'Promise does not exist for the provided text',
            )
          )
            return null

          // Tag logs with the verified-bot status so we can filter bot vs.
          // human traffic in Grafana.
          log.context = {
            ...log.context,
            verifiedbot: webVitalsContext.verifiedbot,
          }
        }

        // Enrich Core Web Vitals measurements with the perf-context fields we
        // previously sent to Noodle (network, device, cache, bot status).
        // Scoped to web-vitals so logs/errors aren't bloated.
        if (item.type === TransportItemType.MEASUREMENT) {
          const measurement = item.payload as MeasurementEvent
          if (measurement.type === 'web-vitals') {
            measurement.context = {
              ...measurement.context,
              ...webVitalsContext,
            }
          }
        }

        if (item.type === TransportItemType.EVENT) {
          const event = item.payload as EventEvent

          // Cloudflare injects its own CSP policies that report violations to
          // `cf-csp-endpoint`. Report-only violations are noisy and useless to us;
          // enforced ones are useful to see if Cloudflare starts blocking requests.
          if (isCloudflareReportCspViolation(event)) return null
        }

        return item
      },
    })
  },
})
